import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import React, { useEffect, useRef } from "react";
import { brown } from "../stitches.config";
import {
  currentUserAtom,
  userLocationsAtom,
  updateFaceBlocksAtom,
  useCanvasRef,
  deleteFromFaceBlocksAtom,
  useOnlines,
} from "../jotai/atoms";
import { styled } from "../stitches.config";
import { AnyBlock, FaceBlock as FaceBlockType } from "../types";
import Icon from "../components/Icon";
import { useUserInfo } from "../roomservice/maps";

// how far in pixels you have to scroll the facedock out of viewport to be "yoinked" back
const threshhold = 200;

function elementIsInViewport(elem: HTMLDivElement) {
  const bounding = elem.getBoundingClientRect();

  let innerHeight = window.innerHeight || document.documentElement.clientHeight;
  let innerWidth = window.innerWidth || document.documentElement.clientWidth;
  return (
    bounding.bottom >= -threshhold &&
    bounding.right >= -threshhold &&
    bounding.top <= innerHeight + threshhold &&
    bounding.left <= innerWidth + threshhold
  );
}

function FaceBlock({
  block,
  updateBlock,
}: {
  block: FaceBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  const userId = block.value;
  const [currentUser] = useAtom(currentUserAtom);
  const [userInfo] = useUserInfo(currentUser?.id);
  const onlines = useOnlines();
  const [userLocations] = useAtom(userLocationsAtom);
  const canvasRef = useCanvasRef();
  const updateFaceBlocks = useUpdateAtom(updateFaceBlocksAtom);
  const currentUserLocation = userLocations[currentUser!.id];
  const moved = useRef(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const deleteFromFaceBlocks = useUpdateAtom(deleteFromFaceBlocksAtom);

  useEffect(() => {
    updateFaceBlocks(block);
    return () => {
      deleteFromFaceBlocks(block.id);
    };
  }, [block, deleteFromFaceBlocks, updateFaceBlocks]);

  function onMouseOver(e: React.MouseEvent<HTMLElement>) {
    // If already in use, locked, or you're locked elsewhere
    const elsewhere =
      currentUserLocation &&
      currentUserLocation.block &&
      currentUserLocation.block !== block.id &&
      currentUserLocation.locked;

    const isCurrentUserStreamOff = !userInfo?.video;

    if (
      (block.value && onlines.includes(block.value)) ||
      block.lockedBy ||
      elsewhere ||
      isCurrentUserStreamOff
    ) {
      return;
    }

    updateBlock({
      value: currentUser!.id,
    });
  }

  function onMouseLeave(e: React.MouseEvent<HTMLElement>) {
    if (block.value !== currentUser!.id) return;
    if (block.lockedBy) return;
    updateBlock({
      value: undefined,
    });
  }

  // sometimes mouseleave doesn't trigger because of framerate
  // brute force a bit with canvasRef
  useEffect(() => {
    if (!canvasRef) return;
    if (block.value !== currentUser!.id) return;
    if (block.lockedBy) return;
    function exitBlock(e: MouseEvent) {
      if (e.target !== e.currentTarget) return;
      updateBlock({
        value: undefined,
      });
    }

    canvasRef.addEventListener("mousemove", exitBlock);
    return () => {
      canvasRef.removeEventListener("mousemove", exitBlock);
    };
  }, [canvasRef, updateBlock, block, currentUser]);

  function onClick(e: React.MouseEvent<HTMLElement>) {
    if (moved.current) return; // click event doesn't cancel if you are moving the block
    if (block.value !== currentUser!.id) return; // docked by someone else
    if (block.lockedBy && block.lockedBy.id !== currentUser!.id) return; // locked by someone else
    // If unlocked, lock, otherwise, unlock (by setting undefined)
    updateBlock({
      lockedBy: block.lockedBy ? undefined : currentUser,
    });
  }

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    moved.current = true;
  }

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    moved.current = false;
  }

  // Unlock if person leaves
  useEffect(() => {
    if (!block.lockedBy) {
      return;
    }

    // ignore when onlines are still loading, at least one value should be here (current user)
    if (onlines.length === 0) {
      return;
    }

    if (!onlines.includes(block.lockedBy.id)) {
      updateBlock({
        value: undefined,
        lockedBy: undefined,
      });
    }
  }, [onlines, updateBlock, block]);

  // unlock if block scrolls out of viewport
  useEffect(() => {
    function onScroll() {
      if (blockRef.current && !elementIsInViewport(blockRef.current)) {
        updateBlock({
          value: undefined,
          lockedBy: undefined,
        });
      }
    }

    if (blockRef && userId && currentUser && userId === currentUser?.id) {
      window.addEventListener("scroll", onScroll);
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [blockRef, currentUser, updateBlock, userId]);

  return (
    <VideoContainer
      ref={blockRef}
      onMouseOver={onMouseOver}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {userId ? null : (
        <Empty>
          <HoverIcon />
        </Empty>
      )}
    </VideoContainer>
  );
}

export default FaceBlock;

const HoverIcon = styled(() => <Icon icon="FaceDock" color={brown} />, {
  transform: "scale(1.5)",
});

const Empty = styled("div", {
  textAlign: "center",
  height: "100%",
  display: "grid",
  placeItems: "center",
  padding: "$2",
  fontSize: ".75rem",
});

const VideoContainer = styled("div", {
  position: "relative",
  userSelect: "none",
});
